import { useGraphQLQuery } from '@betterment/js-runtime';
import { Box } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';

import { AccountsNav } from './components/accounts/AccountsNav';
import { ActivityNav } from './components/activity/ActivityNav';
import { BenefitsAndRewardsNav } from './components/benefits-and-rewards/BenefitsAndRewardsNav';
import { SidebarHeaderDocument } from './components/header/graphql/query.sidebarHeader.generated';
import { SidebarHeader } from './components/header/SidebarHeader';
import { LinkButton } from './components/LinkButton';
import { NavBackgroundScrim } from './components/NavBackgroundScrim';
import { SettingsNav } from './components/settings/SettingsNav';
import { isSidebarVisible } from './hooks/isSidebarVisible';
import { useOutsideClickRef } from './hooks/useOutsideClickRef';
import { useExpandSidebar, useSidebarMode } from './hooks/useSidebarMode';

import NavHeader from '../Shared/components-v2/nav-header/nav-header';
import { urlBuilder } from '../Shared/urlBuilder';

export default function Sidebar() {
  const [sidebarMode] = useSidebarMode();
  const expandSidebar = useExpandSidebar();
  const outsideClickRef = useOutsideClickRef();
  const isVisible = isSidebarVisible(sidebarMode);

  const { loading, data } = useGraphQLQuery(SidebarHeaderDocument, {
    fetchPolicy: 'cache-first',
    throwOnError: false,
  });

  const advisor = data?.advisor;
  const hasLogo = !loading && !advisor;

  return (
    <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
      {(sidebarMode === 'expanded' || sidebarMode === 'collapsed') && (
        <NavHeader hasLogo={hasLogo} onHamburgerClick={expandSidebar} />
      )}

      {sidebarMode === 'expanded' && <NavBackgroundScrim />}

      <Box
        id="navigation-sidebar"
        ref={outsideClickRef}
        css={{
          display: isVisible ? 'block' : 'none',
          height: '100%',
          width: '100%',
          position: 'fixed',
          top: 0,
          overflow: 'auto',
          pb: 'space.300',
          px: 'space.100',
          md: { width: '240px' },
          backgroundColor: 'primitives.navy.100',
          _light: {
            backgroundColor: 'primitives.navy.100',
          },
          _dark: {
            backgroundColor: 'background.surface',
            md: {
              borderRightStyle: 'solid',
              borderRightWidth: 'thin',
              borderRightColor: 'border',
            },
          },
        }}
      >
        <SidebarHeader loading={loading} advisor={advisor} />
        <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
          <AccountsNav />
        </ErrorBoundary>
        <LinkButton to={urlBuilder.addGoal.absolute()}>Open new account</LinkButton>
        <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
          <ActivityNav />
        </ErrorBoundary>
        <ErrorBoundary fallbackType="inline" feature="sidebar" owner="user_engagement">
          <BenefitsAndRewardsNav />
        </ErrorBoundary>
        <SettingsNav />
      </Box>
    </ErrorBoundary>
  );
}
